import React, { useMemo } from 'react';
import { EvoMfeApp } from '@user-interfaces/evo-app-container';
import { useFeedbackCollectorParams } from '@/hooks/useFeedbackCollectorUrl';
import type { FeatureFeedbackCollectorProps } from '@/types/feedbackCollector';

/**
 * A reusable feedback collector component that wraps the EvoMfeApp
 * micro-frontend host for the evo-feature-feedback-collector.
 *
 * This component automatically sources:
 * - userId from TimesheetContext (currentEmployee)
 * - customerName from CompanyContext (currentCompany)
 * - product, apiHost, and accountToken from configuration
 *
 * Only the `feature` prop needs to be specified per usage.
 *
 * Integration follows the Evo Application Integration guide:
 * https://compass.accessacloud.com/docs/Observability/pervasive-telemetry/feature-feedback/integration/evo-integration
 *
 * @example
 * ```tsx
 * <FeatureFeedbackCollector feature="Homepage" />
 * <FeatureFeedbackCollector feature="Timesheet Quick Entry" />
 * ```
 */
export const FeatureFeedbackCollector: React.FC<FeatureFeedbackCollectorProps> = ({
  feature,
  className,
}) => {
  const params = useFeedbackCollectorParams(feature);

  // Memoize componentParams so a new object reference isn't created on every
  // render.  Without this, parent re-renders (e.g. timesheet week navigation)
  // cause EvoMfeApp to destroy and recreate the oc-component DOM element,
  // producing a visible layout glitch and shadow-DOM errors.
  const componentParams = useMemo(() => {
    if (!params) return null;
    return {
      ...params,
      reDisplayAfterDays: 0, // Always show for testing
    };
  }, [params]);

  if (!componentParams) {
    return null;
  }

  return (
    <EvoMfeApp
      componentName="evo-feature-feedback-collector"
      componentVersion="1"
      componentParams={componentParams}
      className={className}
    />
  );
};

export default FeatureFeedbackCollector;
